import { useQuery } from "@tanstack/react-query";
import { useSession } from "../../lib/session";
import { fetchMyVehiclePrompts, fetchMyOdometerPrompts } from "../vehicles/api";
import { fetchLicenceQueue, fetchMyLicenceStatus, fetchPendingApprovals } from "../people/api";
import { licenceActionNeeded } from "../people/LicenceCheckPrompts";
import { fetchItemsRequiringAttention } from "../admin/api";
import { fetchMyActionItems } from "../mentions/api";

/**
 * One count for everything outstanding, so the To Do badge and the To Do
 * page cannot disagree.
 *
 * These are the same four queries the home screen already ran as four
 * separate prompt cards, with the SAME query keys, fetchers and staleTime
 * — react-query dedupes on the key, so mounting this hook next to those
 * cards costs no extra round trips and reads whatever they already
 * fetched.
 *
 * Every source is filtered server-side by permission: my_vehicle_prompts,
 * my_odometer_prompts, my_pending_approvals and items_requiring_attention
 * each decide for themselves who sees what. So the badge is genuinely
 * "things YOU can act on", not a count of the company's problems, and
 * someone without a grant sees zero rather than a number they cannot do
 * anything about. The `enabled` gates below only avoid a pointless
 * request; they are not the access control.
 */
export interface TodoCounts {
  vehicleChecks: number;
  odometer: number;
  approvals: number;
  attention: number;
  /** 1 when this person owes a DVLA check code, else 0. */
  licence: number;
  /** Codes waiting for a vehicle admin to review. */
  licenceQueue: number;
  /** Open mentions/actions assigned to me. */
  actions: number;
  /** What the badge shows. */
  total: number;
  /** True until every source has answered — the badge stays hidden rather
   * than flashing a wrong number on the way up. */
  isLoading: boolean;
}

export function useTodoCounts(): TodoCounts {
  const { can, profile } = useSession();

  const vehicles = useQuery({
    queryKey: ["my-vehicle-prompts"],
    queryFn: fetchMyVehiclePrompts,
    staleTime: 60_000,
    enabled: can("can_view_vehicles"),
  });
  const odometer = useQuery({
    queryKey: ["my-odometer-prompts"],
    queryFn: fetchMyOdometerPrompts,
    staleTime: 60_000,
    enabled: can("can_view_vehicles"),
  });
  const approvals = useQuery({
    queryKey: ["pending-approvals"],
    queryFn: fetchPendingApprovals,
    staleTime: 60_000,
    enabled: can("can_manage_people"),
  });
  const attention = useQuery({
    queryKey: ["items-requiring-attention"],
    queryFn: fetchItemsRequiringAttention,
    staleTime: 30_000,
  });
  const licence = useQuery({
    queryKey: ["my-licence-status"],
    queryFn: fetchMyLicenceStatus,
    staleTime: 60_000,
    enabled: can("can_view_vehicles"),
  });
  const licenceQueue = useQuery({
    queryKey: ["licence-queue"],
    queryFn: fetchLicenceQueue,
    staleTime: 60_000,
    enabled: can("can_manage_vehicles"),
  });
  // Same query key MyActionsCard uses — react-query dedupes.
  const actions = useQuery({
    queryKey: ["my-action-items", profile?.id],
    queryFn: () => fetchMyActionItems(profile!.id),
    staleTime: 30_000,
    enabled: !!profile,
  });

  /* A query that is disabled or errored contributes nothing rather than
     blocking: a broken vehicle RPC should not hide a person waiting to be
     let into the app. */
  const vehicleChecks = vehicles.data?.length ?? 0;
  const odo = odometer.data?.length ?? 0;
  const appr = approvals.data?.length ?? 0;
  const attn = attention.data?.length ?? 0;
  const lic = licenceActionNeeded(licence.data?.due_state) ? 1 : 0;
  const licQ = licenceQueue.data?.length ?? 0;
  const act = actions.data?.length ?? 0;

  return {
    vehicleChecks,
    odometer: odo,
    approvals: appr,
    attention: attn,
    licence: lic,
    licenceQueue: licQ,
    actions: act,
    total: vehicleChecks + odo + appr + attn + lic + licQ + act,
    isLoading:
      vehicles.isLoading || odometer.isLoading || approvals.isLoading || attention.isLoading ||
      licence.isLoading || licenceQueue.isLoading || actions.isLoading,
  };
}
